import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, UserCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageSEO } from '../components/SEO/PageSEO';
import Mixpanel from '../utils/mixpanel';

const sections = [
  {
    id: 'introduction',
    title: 'Introduction',
    content: `At LetsHost, we are committed to protecting your privacy and ensuring transparency about how we collect, use, share, and safeguard your information. Whether you're booking a venue or browsing our platform, we commit to handling your data securely and responsibly.`
  },
  {
    id: 'controls',
    title: '1. Who Controls Your Information',
    content: `LetsHost is the sole controller of your personal information collected through our platform. By using our services, you consent to the collection and processing of your information as described in this Privacy Policy.`
  },
  {
    id: 'collection',
    title: '2. Information We Collect',
    content: (
      <>
        <h3 className="text-base font-semibold mt-4 mb-2 text-stone-800">2.1 Information You Provide Directly</h3>
        <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
          <li>Account Information (name, email, phone, etc.)</li>
          <li>Event Details (venue preferences, guest count, requirements)</li>
          <li>Payment Information (card details and billing address, processed securely)</li>
          <li>Communications (support requests, emails, etc.)</li>
        </ul>

        <h3 className="text-base font-semibold mt-4 mb-2 text-stone-800">2.2 Information Automatically Collected</h3>
        <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
          <li>Usage Data (IP address, browser type, device details, activity)</li>
          <li>Location Data (approximate location via IP)</li>
          <li>Cookies and Similar Technologies (refer to our Cookie Policy)</li>
        </ul>

        <h3 className="text-base font-semibold mt-4 mb-2 text-stone-800">2.3 Information from Third Parties</h3>
        <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
          <li>Data from our service providers (Vercel, AI tools, Airtable, Gmail)</li>
        </ul>
      </>
    )
  },
  {
    id: 'usage',
    title: '3. How We Use Your Information',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>To facilitate bookings and match you with the right venues</li>
        <li>To personalize your experience and improve our platform</li>
        <li>To provide customer support and respond to inquiries</li>
        <li>To send account notifications and updates (promotional offers only with consent)</li>
        <li>To ensure security, prevent fraud, and monitor for suspicious activity</li>
        <li>To conduct analytics and improve our services</li>
      </ul>
    )
  },
  {
    id: 'sharing',
    title: '4. Sharing and Disclosure',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>With venue partners to confirm bookings</li>
        <li>With service providers under strict confidentiality (payment processors, hosting, etc.)</li>
        <li>When required by law or to protect our rights</li>
        <li>In the event of a business transfer, with prior notice</li>
      </ul>
    )
  },
  {
    id: 'rights',
    title: '5. Your Rights and Choices',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>Access, correct, or request deletion of your data (subject to legal requirements)</li>
        <li>Opt out of non-essential data collection and marketing - contact <a href="mailto:privacy@letshost.nyc?subject=Privacy%20Policy%20-%20Data%20Collection%20Opt-Out" className="text-stone-700 underline hover:text-stone-900">privacy@letshost.nyc</a></li>
        <li>Request a copy of your data or deletion of records (except legally required retention)</li>
      </ul>
    )
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>We retain data only as long as necessary for service provision and legal compliance</li>
        <li>Data is securely deleted when no longer required, except where legally necessary</li>
      </ul>
    )
  },
  {
    id: 'security',
    title: '7. Security',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>We use encryption, access controls, and regular security audits</li>
        <li>While we implement strong security measures, no online system is completely infallible</li>
      </ul>
    )
  },
  {
    id: 'cookies',
    title: '8. Cookies and Tracking',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>Cookies enhance browsing experience and help analyze usage</li>
        <li>Manage cookie preferences via browser settings</li>
        <li>See our Cookie Policy for details</li>
      </ul>
    )
  },
  {
    id: 'thirdparty',
    title: '9. Third-Party Services',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>We use trusted services for hosting, venue matching, data management, and communications</li>
        <li>All providers are bound by confidentiality and maintain their own privacy policies</li>
      </ul>
    )
  },
  {
    id: 'international',
    title: '10. International Transfers',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>While we operate in NYC, our platform is accessible worldwide</li>
        <li>International data is processed in the U.S. under appropriate safeguards</li>
      </ul>
    )
  },
  {
    id: 'children',
    title: "11. Children's Privacy",
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>We don't knowingly collect data from children under 13</li>
        <li>We will delete any such data if discovered</li>
      </ul>
    )
  },
  {
    id: 'changes',
    title: '12. Policy Updates',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>Policy updates reflect changes in practices or legal requirements</li>
        <li>Significant changes will be communicated via email or platform notices</li>
      </ul>
    )
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    content: (
      <ul className="list-disc pl-6 text-stone-500 space-y-2 text-sm">
        <li>Privacy questions: <a href="mailto:privacy@letshost.nyc?subject=Privacy%20Policy%20Question" className="text-stone-700 underline hover:text-stone-900">privacy@letshost.nyc</a></li>
        <li>General inquiries: <a href="mailto:events@letshost.nyc?subject=General%20Inquiry" className="text-stone-700 underline hover:text-stone-900">events@letshost.nyc</a></li>
      </ul>
    )
  }
];

export default function Privacy() {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (id) => {
    const isOpening = !openSections[id];
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
    
    if (isOpening) {
      Mixpanel.track('Privacy Section Opened', { 
        section: id,
        sectionTitle: sections.find(section => section.id === id)?.title || id
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSEO
        title="Privacy Policy"
        description="Learn how LetsHost protects your privacy and personal information. Our commitment to data security and transparency in our venue matching services."
        path="/privacy"
      />
      <Navbar />
      <main className="flex-grow">
        {/* ───────── HERO ───────── */}
        <section className="pb-8 sm:pb-12 px-5 sm:px-10 lg:px-16" style={{ backgroundColor: '#F5F0EB' }}>
          <div className="max-w-7xl mx-auto mt-2 sm:mt-6 md:mt-8">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <span className="text-xs tracking-[0.16em] uppercase text-stone-400 font-sans font-medium block mb-5">
                Privacy
              </span>
              <h1
                className="text-[2.75rem] sm:text-[3.5rem] md:text-[4.2rem] font-bold text-gray-900 leading-[1.06] mb-4 max-w-2xl"
                style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
              >
                Privacy Policy
              </h1>
              <p className="text-base sm:text-lg text-stone-500 leading-relaxed mb-8 max-w-md">
                Last updated: January 2024
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                {[
                  { icon: Shield, label: 'Your Privacy First' },
                  { icon: Lock, label: 'Data Protection' },
                  { icon: UserCheck, label: 'Your Control' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-stone-200/60">
                    <div className="flex items-center gap-3">
                      <div className="bg-stone-100 p-2 rounded-lg">
                        <Icon className="w-5 h-5 text-stone-500" />
                      </div>
                      <h3
                        className="text-base font-semibold text-stone-800"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {label}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ───────── CONTENT ───────── */}
        <section className="max-w-4xl mx-auto px-5 sm:px-10 py-10 sm:py-14">
          <h2
            className="text-2xl font-bold mb-8 text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            How We Protect Your Information
          </h2>

          <div className="divide-y divide-stone-200">
            {sections.map(section => (
              <div key={section.id} id={section.id}>
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between py-5 text-left hover:bg-stone-50 rounded-lg px-3 transition-colors"
                >
                  <h2
                    className="text-lg font-semibold text-stone-800"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {section.title}
                  </h2>
                  {openSections[section.id] ? (
                    <ChevronUp className="w-5 h-5 text-stone-400 shrink-0 ml-4" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-stone-400 shrink-0 ml-4" />
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openSections[section.id] ? 'max-h-[2000px] opacity-100 mb-4' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pb-4 px-3">
                    {typeof section.content === 'string' ? (
                      <p className="text-stone-500 text-sm leading-relaxed">{section.content}</p>
                    ) : (
                      section.content
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
